import { createSlice } from "@reduxjs/toolkit";
import { makeThunk } from "helpers/makeThunk";
import { handleCommonCases } from "helpers/thunkHandler";

export const createCrudSlice = ({
  name,
  urls,
  initialFilters = {},
  additionalThunks = {},
  additionalReducers = {},
  initialState: customInitialState = {},
  hasDropdownList = false,
  defaultDropdownData = [],
}) => {
  // Standard CRUD thunks

  const thunks = {
    getList: urls.getList
      ? makeThunk({
          type: `${name}/getList`,
          url: urls.getList,
          method: urls.getListMethod || "post",
        })
      : null,

    add: urls.add
      ? makeThunk({
          type: `${name}/add`,
          url: urls.add,
          method: urls.addMethod || "post",
        })
      : null,

    update: urls.update
      ? makeThunk({
          type: `${name}/update`,
          url: urls.update,
          method: urls.updateMethod || "put",
        })
      : null,

    delete: urls.delete
      ? makeThunk({
          type: `${name}/delete`,
          url: urls.delete,
          method: "delete",
        })
      : null,

    updateStatus: urls.updateStatus
      ? makeThunk({
          type: `${name}/updateStatus`,
          url: urls.updateStatus,
          method: "patch",
        })
      : null,

    getDetails: urls.getDetails
      ? makeThunk({
          type: `${name}/getDetails`,
          url: (id) => `${urls.getDetails}/${id}`,
          method: "get",
        })
      : null,

    // Dropdown list thunk
    getDropdownList:
      hasDropdownList && urls.getDropdownList
        ? makeThunk({
            url: urls.getDropdownList,
            method: "get",
          })
        : null,
  };

  // Add custom thunks
  Object.entries(additionalThunks).forEach(([key, thunkConfig]) => {
    thunks[key] = makeThunk(thunkConfig);
  });

  // Standard initial state
  const initialState = {
    loading: false,
    isLoading: false,
    isDeleting: false,
    loadingItemId: null,
    totalCount: null,
    totalPages: null,
    currentPage: null,
    items: [],
    itemDetails: {},
    filters: {
      keyword: "",
      status: -1,
      orderBy: -1,
      dates: [],
      pageNo: 1,
      perPage: 50,
      ...initialFilters,
    },
    // Add dropdown list support if enabled
    ...(hasDropdownList && {
      itemsList: defaultDropdownData || [],
      isLoadingDropdown: false,
    }),
    ...customInitialState,
  };

  // Create slice
  const slice = createSlice({
    name,
    initialState,
    reducers: {
      setFilter: (state, action) => {
        const { key, value } = action.payload;
        state.filters[key] = value;
      },

      resetFilters: (state) => {
        state.filters = initialState.filters;
      },

      clearItemDetails: (state) => {
        state.itemDetails = {};
      },

      ...additionalReducers,
    },

    extraReducers: (builder) => {
      // Get list (with pagination)
      if (thunks.getList) {
        handleCommonCases(builder, thunks.getList, {
          onFulfilled: (state, action) => {
            const { data, totalCount, totalPages, currentPage } =
              action.payload || {};
            state.items = data || [];
            state.totalCount = totalCount;
            state.totalPages = totalPages;
            state.currentPage = currentPage;
          },
        });
      }

      // Get dropdown list (no pagination)
      if (thunks.getDropdownList) {
        handleCommonCases(builder, thunks.getDropdownList, {
          loadingKey: "isLoadingDropdown",
          onFulfilled: (state, action) => {
            const { data } = action.payload || {};
            state.itemsList = data || [];
          },
        });
      }

      // Add
      if (thunks.add) {
        handleCommonCases(builder, thunks.add, {
          loadingKey: "isLoading",
        });
      }

      // Update
      if (thunks.update) {
        handleCommonCases(builder, thunks.update, {
          loadingKey: "isLoading",
        });
      }

      // Delete
      if (thunks.delete) {
        handleCommonCases(builder, thunks.delete, {
          loadingKey: "isDeleting",
          onFulfilled: (state, action) => {
            const deletedId = action.meta.arg.id;
            state.items = state.items.filter((item) => item._id !== deletedId);
            if (state.totalCount !== null) {
              state.totalCount = state.totalCount - 1;
            }
          },
        });
      }

      // Update status (optional)
      if (thunks.updateStatus) {
        handleCommonCases(builder, thunks.updateStatus, {
          loadingKey: "isLoading",
          onPending: (state, action) => {
            state.loadingItemId = action.meta.arg._id;
          },
          onFulfilled: (state, action) => {
            const { id, status } = action.meta.arg;
            const item = state.items.find((item) => item._id === id);
            if (item) {
              item.status = status;
            }
            state.loadingItemId = null;
          },
          onRejected: (state) => {
            state.loadingItemId = null;
          },
        });
      }

      // Get details (optional)
      if (thunks.getDetails) {
        handleCommonCases(builder, thunks.getDetails, {
          onPending: (state) => {
            state.itemDetails = {};
          },
          onFulfilled: (state, action) => {
            const { data } = action.payload || {};
            state.itemDetails = data || {};
          },
        });
      }

      Object.entries(additionalThunks).forEach(([key, thunkConfig]) => {
        if (thunks[key]) {
          const { loadingKey } = thunkConfig;

          handleCommonCases(builder, thunks[key], {
            loadingKey,
            onFulfilled: (state, action) => {
              const stateKey = thunkConfig.stateKey || key;

              if (state[stateKey] !== undefined) {
                state[stateKey] = action.payload?.data || action.payload;
                if (
                  stateKey === "systemSettings" ||
                  stateKey === "appliedCouponHistory"
                ) {
                  state[stateKey] = action.payload;
                }
                if (stateKey === "transactions") {
                  const { data, totalCount, totalPages, currentPage } =
                    action.payload || {};
                  state[stateKey] = data;
                  state.totalCount = totalCount;
                  state.totalPages = totalPages;
                  state.currentPage = currentPage;
                }
              }
            },
          });
        }
      });
    },
  });

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    thunks,
  };
};
